import React, { useState, useMemo, useEffect } from 'react';
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, CircularProgress } from '@mui/material';
import { styled } from "@mui/system";
import styles from './OverviewTable.module.css';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CustomPagination from 'Samarth Module/components/CustomPagination/CustomPagination';
import { IoMdInformationCircleOutline } from 'react-icons/io';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#2036C7",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 400,
        border: "1px solid #EAECF0",

    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
    },
}));

const StyledTableRow = styled(TableRow)({});

function OverviewTable({ tableData, columns = [], paginationData, sortingData, handlePageChange }) {
    const [sortColumn, setSortColumn] = useState(sortingData?.sortCol || null);
    const [sortDirection, setSortDirection] = useState(sortingData?.sortDirection || 'ASC'); // 'ASC' or 'DESC'
    const [page, setPage] = useState(paginationData?.page ? paginationData.page : 1); // API uses 1-based, MUI uses 0-based
    const [rowsPerPage, setRowsPerPage] = useState(paginationData?.rowsPerPage || 15);
    const [isLoading, setIsLoading] = useState(false);
    
    // Reset loading state when tableData changes (API response )
    useEffect(() => {
        setIsLoading(false);
    }, [tableData]);

    // Handle column header click for sorting
    const handleSort = (col, direction) => {
        // Update local state
        setSortColumn(col);
        setSortDirection(direction);
        setIsLoading(true); // Show loader
        // Reset to first page and trigger API call with sorting
        handleChangePage(rowsPerPage, 1, col, direction);
    };

    const paginatedData = useMemo(() => {
        if (paginationData?.totalRecords) {
            return tableData; // Data is already paginated from server
        }
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return tableData.slice(startIndex, endIndex);
    }, [tableData, page, rowsPerPage, paginationData]);

    // Pagination handlers
    const handleChangePage = (rowsPerPage, newPage, sortCol, sortDirection) => {
        setPage(newPage);
        console.log("Page change triggered. New page:", rowsPerPage, newPage, "Sort column:", sortCol, "Sort direction:", sortDirection);
        handlePageChange(rowsPerPage, newPage, sortCol, sortDirection);
    };

    // Handle page change from CustomPagination (1-based)
    const handleCustomPageChange = (newPage, newPageSize) => {
        setPage(newPage); // Convert to 0-based for internal state
        setIsLoading(true); // Show loader
        handlePageChange(newPageSize, newPage, sortColumn, sortDirection);
    };

    // Handle rows per page change
    const handleCustomRowsPerPageChange = (newPageSize) => {
        setRowsPerPage(newPageSize);
        setPage(1); // Reset to first page
        setIsLoading(true); // Show loader
        handlePageChange(newPageSize, 1, sortColumn, sortDirection);
    };

    // Calculate total pages
    const totalRecords = paginationData?.totalRecords || tableData.length;
    const totalPages = Math.ceil(totalRecords / rowsPerPage);

    // Styling function for data cells
    const dataStyling = (col, value) => {
        let style = {};
        if (["Priority Rank", "PO Priority", "PO Expiry Date", "PO Validity"].includes(col.label)) {
            style = { color: '#1A2025 !important', fontWeight: '600 !important', backgroundColor: '#F1F5F7 !important' };
        } else if (typeof value === 'number') {
            style = { color: '#0066CC !important', fontWeight: '600 !important' };
        } else if(col.label === 'Ideal | Open orders') {
            style = { color: '#0066CC !important', fontWeight: '600 !important' };
        } else if (col.label === "Stock to Norm %") {
            style = { color: '#1A2025 !important', fontWeight: '600 !important' };
        } else {
            style = { color: '#1A2025 !important', fontWeight: '400 !important' };
        }
        return style;
    };

    const dohBucketStyling = (bucket) => {
        if(!bucket) return {};
        switch (bucket) {
            case '0 Days':
                return { color: '#DA0000',background: '#EA3A3D1A', };
            case bucket.includes('>'):
                return { color: '#667F93', background: '#F6F7F01A', };
            default:
                return { color: '#FF9650',background: '#FFDCC566', };
        }
    };

    return (
        <>
            <TableContainer className={styles.tableContainer}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.filter(col => col.visibility).map((col, colIdx) => (
                                <StyledTableCell
                                    key={col.label}
                                    sx={{
                                        textAlign: "center",
                                        borderTopLeftRadius: colIdx === 0 ? '8px' : '0',
                                        borderTopRightRadius: colIdx === columns.filter(col => col.visibility).length - 1 ? '8px' : '0',
                                        cursor: col.sortable ? 'pointer' : 'default'
                                    }}
                                    onClick={() => handleSort(col.label, sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                                    colSpan={col.label === "Count of Priority for Dispatch" ? 3 : 1}
                                    rowSpan={col.label === "Count of Priority for Dispatch" ? 1 : 2}
                                >
                                    <div className={styles.labelHeader}>
                                        <span className={styles.label} style={{width: col.label === 'DOH' ? '5rem' : 'auto' }}>{col.label}{col.label.trim() === 'Ideal | Open orders' && <IoMdInformationCircleOutline style={{ cursor: 'pointer' }} />}</span>
                                        {col.sortable && !["Ideal | Open orders","Count of Priority for Dispatch" ].includes(col.label) && (
                                            <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '4px' }}>
                                                {sortColumn === col.label ? (
                                                    sortDirection === 'ASC' ? (
                                                        <ArrowDropUpIcon sx={{ fontSize: 18, color: '#fff' }} />
                                                    ) : (
                                                        <ArrowDropDownIcon sx={{ fontSize: 18, color: '#fff' }} />
                                                    )
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', opacity: 0.5 }}>
                                                        <ArrowDropUpIcon sx={{ fontSize: 18, color: '#fff', marginBottom: '-8px' }} />
                                                        <ArrowDropDownIcon sx={{ fontSize: 18, color: '#fff' }} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </StyledTableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {columns.filter(col => col.visibility).map((col, colIdx) => (
                                col.label === "Count of Priority for Dispatch" ? (
                                    <React.Fragment key={colIdx}>
                                        <StyledTableCell sx={{ textAlign: 'center' }}>High</StyledTableCell>
                                        <StyledTableCell sx={{ textAlign: 'center' }}>Medium</StyledTableCell>
                                        <StyledTableCell sx={{ textAlign: 'center' }}>Low</StyledTableCell>
                                    </React.Fragment>
                                ) : null
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <StyledTableCell colSpan={columns.filter(col => col.visibility).length + 2}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center', 
                                        padding: '40px 0',
                                        minHeight: '200px'
                                    }}>
                                        <CircularProgress size={40} />
                                    </div>
                                </StyledTableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((item, idx) => (
                                <React.Fragment key={idx}>
                                    <StyledTableRow>
                                        {columns.filter(col => col.visibility).map((col, colIdx) => {
                                            if (col.label === "Count of Priority for Dispatch") {
                                                // Render 3 cells for High, Medium, Low
                                                return (
                                                    <React.Fragment key={colIdx}>
                                                        <StyledTableCell
                                                            sx={{
                                                                textAlign: 'center',
                                                                ...dataStyling(col, item[col.label]['High'])
                                                            }}
                                                        >
                                                            {item[col.label]['High'] || 0}
                                                        </StyledTableCell>
                                                        <StyledTableCell
                                                            sx={{
                                                                textAlign: 'center',
                                                                ...dataStyling(col, item[col.label]['Medium'])
                                                            }}
                                                        >
                                                            {item[col.label]['Medium'] || 0}
                                                        </StyledTableCell>
                                                        <StyledTableCell
                                                            sx={{
                                                                textAlign: 'center',
                                                                ...dataStyling(col, item[col.label]['Low'])
                                                            }}
                                                        >
                                                            {item[col.label]['Low'] || 0}
                                                        </StyledTableCell>
                                                    </React.Fragment>
                                                );
                                            } else {
                                                // Regular column
                                                return (
                                                    <StyledTableCell
                                                        key={colIdx}
                                                        sx={{
                                                            borderBottomLeftRadius: idx === paginatedData.length - 1 && colIdx === 0 ? '8px' : '0',
                                                            borderBottomRightRadius: idx === paginatedData.length - 1 && colIdx === columns.filter(col => col.visibility).length - 1 ? '8px' : '0',
                                                            ...dataStyling(col, item[col.label])
                                                        }}
                                                    >
                                                        {col.label === 'DOH' ?
                                                            <div className={styles.dohStyling}>
                                                                {item['DOH']}<span className={styles.dohBucket} style={dohBucketStyling(item['DOH Bucket'])}>{item['DOH Bucket']}</span>
                                                            </div> :
                                                            item[col.label]
                                                        }
                                                    </StyledTableCell>
                                                );
                                            }
                                        })}
                                    </StyledTableRow>
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <CustomPagination
                page={page}
                pageSize={rowsPerPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                onPageChange={handleCustomPageChange}
                onRowsPerPageChange={handleCustomRowsPerPageChange}
            />
        </>
    );
}

export default OverviewTable;